/// <amd-module name="Test/common-util/Common.Utils" />
// The file should contain strict utility class (one that have only static methods).
// Also, these should be worthy of being called common and should be usable across features
import * as Common from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Resources from "Test/common-util/Resources";
import { getTFSPageData } from "Tfs/Platform/Page";
import { format, localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

export class TestReportDataParser {

    // Time span is serialized in the following format: dd.hh:min:s.ms
    private static _getDurationObject(duration: string): Common.IDuration {
        // Check if we have days in the duration.
        let durationParts = duration.split(".");

        let days = 0;
        if (durationParts[0] && durationParts[1] && durationParts[1].indexOf(":") > 0) {
            // This means we have days in the duration. The duration is something like 2.03:04:50 or 1.04:50:47.6700
            days = parseInt(durationParts[0]);
            duration = durationParts[1];
        }

        duration = duration.replace(".", ":");
        durationParts = duration.split(":");

        let milliSeconds = 0;
        if (durationParts[3]) {
            const seconds = parseFloat("." + durationParts[3]);
            milliSeconds = Math.round(seconds * 1000);
        }

        const durationObj = {
            days: days,
            hours: durationParts[0] ? parseInt(durationParts[0]) : 0,
            minutes: durationParts[1] ? parseInt(durationParts[1]) : 0,
            seconds: durationParts[2] ? parseInt(durationParts[2]) : 0,
            milliseconds: milliSeconds
        };

        return durationObj;
    }
    public static getPercentageInDisplayFormat(value: number, precision: number = 2): string {
        return format("{0}%", TestReportDataParser.getCustomizedDecimalValueInCurrentLocale(value, precision));
    }

    public static getPercentageInDisplayFormatWithoutSymbol(value: number, precision: number = 2): string {
        return TestReportDataParser.getCustomizedDecimalValueInCurrentLocale(value, precision);
    }

    public static parseDuration(duration: string): string {
        let durationString: string, durationObj: Common.IDuration;

        durationObj = TestReportDataParser._getDurationObject(duration);
        durationString = "";
        if (durationObj.days > 0) {
            durationString = localeFormat(
                "{0} {1}",
                localeFormat(Resources.DayFormat, durationObj.days),
                durationObj.hours > 0 ? localeFormat(Resources.HourFormat, durationObj.hours) : ""
            );
        } else if (durationObj.hours > 0) {
            durationString = localeFormat(
                "{0} {1}",
                localeFormat(Resources.HourFormat, durationObj.hours),
                durationObj.minutes > 0 ? localeFormat(Resources.MinuteFormat, durationObj.minutes) : ""
            );
        } else if (durationObj.minutes > 0) {
            durationString = localeFormat(
                "{0} {1}",
                localeFormat(Resources.MinuteFormat, durationObj.minutes),
                durationObj.seconds > 0
                    ? localeFormat(
                          Resources.SecondFormat,
                          durationObj.seconds === Math.round(durationObj.seconds) ? durationObj.seconds.toString() : durationObj.seconds.toFixed(2)
                      )
                    : ""
            );
        } else if (durationObj.seconds > 0) {
            durationString = localeFormat(
                "{0} {1}",
                localeFormat(Resources.SecondFormat, durationObj.seconds),
                durationObj.milliseconds > 0 ? localeFormat(Resources.MillisecondFormat, durationObj.milliseconds) : ""
            );
        } else {
            if (durationObj.milliseconds === 0) {
                // Test took less than a ms. Report this as 0s
                durationString = localeFormat(Resources.SecondFormat, 0);
            } else {
                durationString = localeFormat(Resources.MillisecondFormat, durationObj.milliseconds);
            }
        }

        return durationString;
    }

    public static isZeroDuration(duration: string): boolean {
        const durationObj = TestReportDataParser._getDurationObject(duration);
        return (
            durationObj.days === 0 &&
            durationObj.hours === 0 &&
            durationObj.minutes === 0 &&
            durationObj.seconds === 0 &&
            durationObj.milliseconds === 0
        );
    }

    public static parseFailureData(testFailures: TCMContracts.TestFailuresAnalysis): Common.ITestFailureData {
        const failureData: Common.ITestFailureData = {
            newFailures: 0,
            existingFailures: 0,
            totalFailures: 0
        };

        failureData.newFailures = testFailures && testFailures.newFailures ? testFailures.newFailures.count : 0;
        failureData.existingFailures = testFailures && testFailures.existingFailures ? testFailures.existingFailures.count : 0;
        failureData.totalFailures = failureData.newFailures + failureData.existingFailures;

        return failureData;
    }

    public static getDurationInMilliseconds(timespanString: string): number {
        const durationObject = TestReportDataParser._getDurationObject(timespanString);
        return (
            durationObject.days * 24 * 60 * 60 * 1000 +
            durationObject.hours * 60 * 60 * 1000 +
            durationObject.minutes * 60 * 1000 +
            durationObject.seconds * 1000 +
            durationObject.milliseconds
        );
    }

    public static getDurationInSeconds(timespanString: string): number {
        const durationObject = TestReportDataParser._getDurationObject(timespanString);
        return durationObject.days * 24 * 60 * 60 + durationObject.hours * 60 * 60 + durationObject.minutes * 60 + durationObject.seconds;
    }

    public static getDurationInMinutes(timespanString: string): number {
        const durationObject = TestReportDataParser._getDurationObject(timespanString);
        return (
            durationObject.days * 24 * 60 + durationObject.hours * 60 + durationObject.minutes + parseFloat((durationObject.seconds / 60).toFixed(2))
        );
    }

    public static getDurationInHours(timespanString: string): number {
        const durationObject = TestReportDataParser._getDurationObject(timespanString);
        return durationObject.days * 24 + durationObject.hours + parseFloat((durationObject.minutes / 60).toFixed(2));
    }

    public static getCustomizedDecimalValueInCurrentLocale(value: number, precision: number): string {
        const customizedValue = this.getCustomizedDecimalValue(value, precision);

        return customizedValue.toLocaleString();
    }

    public static getCustomizedDecimalValue(value: number, precision = 2): number {
        let fixed = precision;
        fixed = Math.pow(10, fixed);
        return Math.floor(value * fixed) / fixed;
    }
}

export class TCMContractsConverter {
    public static convertShallowTestResultToTestCaseResult(result: TCMContracts.ShallowTestCaseResult): TCMContracts.TestCaseResult {
        return {
            id: result.id,
            durationInMs: result.durationInMs,
            testRun: {
                id: result.runId.toString()
            } as TCMContracts.ShallowReference,
            testCaseReferenceId: result.refId
        } as TCMContracts.TestCaseResult;
    }

    public static ConvertMilliSecondsToReadableFormatForResultSummary(milliSeconds: number): string {
        let hour, minute, second;
        second = Math.floor(milliSeconds / 1000);
        milliSeconds = Math.floor(milliSeconds % 1000);
        minute = Math.floor(second / 60);
        second = second % 60;
        hour = Math.floor(minute / 60);
        minute = minute % 60;

        if (minute < 10) {
            minute = "0" + minute;
        }
        if (second < 10) {
            second = "0" + second;
        }
        const milliSecondsFormat: string = TCMContractsConverter.getMillisecondsFormatForDuration(milliSeconds);

        return format("{0}:{1}:{2}.{3}", hour, minute, second, milliSecondsFormat);
    }

    public static getMillisecondsFormatForDuration(milliSeconds: number): string {
        let millisecondsFormat: string;
        if (milliSeconds < 10) {
            millisecondsFormat = "00" + milliSeconds;
        } else if (milliSeconds < 100) {
            millisecondsFormat = "0" + milliSeconds;
        } else {
            millisecondsFormat = "" + milliSeconds;
        }

        return millisecondsFormat;
    }
}

/**
 * Returns the first element of an array that matches the predicate.
 *
 * @param array Array used to perform predicate.
 * @param predicate The Predicate function.
 * @return The first element that matches the predicate.
 */
export function first<T>(array: T[], predicate?: (value: T) => boolean): T | undefined {
    if (!array || array.length === 0) {
        return undefined;
    }

    if (predicate === undefined) {
        return array[0];
    }

    return array.find(predicate);
}

/**
 * @param arrayA
 * @param arrayB
 * @param comparer
 * @return
 */
export function union<T>(arrayA: T[], arrayB: T[], comparer?: { (a: T, b: T): number }): T[] {
    let result: any[];

    if (!arrayB || arrayB.length === 0) {
        return arrayA;
    }

    result = arrayA.concat(arrayB);
    result = uniqueSort(result, comparer);

    return result;
}

/**
 * Sorts and removes duplicate elements
 *
 * @param array
 * @param comparer
 * @return
 */
export function uniqueSort<T>(array: T[], comparer?: { (a: T, b: T): number }): T[] {
    comparer = comparer || defaultComparer;

    const uniqueArray: T[] = Array.from(new Set(array));

    uniqueArray.sort(comparer);

    return uniqueArray;
}

export function defaultComparer<T>(a: T, b: T): number {
    // weak equality should be enough for default comparer
    /*jslint eqeqeq:false */
    if (a == b) {
        return 0;
    } else if (a > b) {
        return 1;
    } else {
        return -1;
    }
    /*jslint eqeqeq:true */
}

export class IconDetails {
    public iconName: string;
    public className: string;
}

// Compares artfact status and ids, returns true if they are different.
export function hasArtifactChanged(artifactData1: Common.IViewContextData, artifactData2: Common.IViewContextData): boolean {
    if (artifactData1.status !== artifactData2.status) {
        return true;
    }

    if (artifactData1.data.mainData.id !== artifactData2.data.mainData.id) {
        return true;
    }

    if (
        artifactData1.data.subData &&
        artifactData2.data.subData &&
        artifactData1.data.subData.environment.id !== artifactData2.data.subData.environment.id
    ) {
        return true;
    }

    return false;
}

// Gets the project name from pagecontext.
export function getProjectName(pageContext: IVssPageContext): string {
    const tfsPageData = getTFSPageData(pageContext);
    if (tfsPageData && tfsPageData.project) {
        return tfsPageData.project.name;
    }

    return "";
}
